import { Component, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import {
  Router,
  ActivatedRoute,
  RouterModule,
  NavigationStart,
} from "@angular/router";
import {
  DxActionSheetModule,
  DxButtonModule,
  DxCheckBoxModule,
  DxContextMenuModule,
  DxDateBoxModule,
  DxFileUploaderModule,
  DxLoadPanelModule,
  DxNumberBoxModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxTabsModule,
  DxTextBoxModule,
  DxToastModule,
} from "devextreme-angular";
import { CommonModule } from "@angular/common";
import { SharedComponentsModule } from "src/app/components/shared.components.module";
import { SharedPipesModule } from "src/app/pipes/shared.pipes.module";
import { ProjectTaskService } from "src/app/services/project-task.service";
import { AppGlobals } from "src/app/app.globals";
import { PageNavigationService } from "src/app/services/page-navigation.service";
import { ProjectService } from "src/app/services/project.service";
import { UtilitiesService } from "src/app/services/utilities.service";
import { ProjectInfoService } from "src/app/services/project-info.service";
import { GridConnectionService } from "src/app/services/grid-connection.service";
import { FeatureFlags } from "src/app/interfaces/enums/feature-flag";
import { Subject, takeUntil } from "rxjs";
import { IconName } from "src/app/helpers/icon-exports";

// Remove staticProjectNavTabs export, and instead define the nav config as a constant for use in the constructor
const projectNavConfig: {
  icon: IconName;
  routerLink: string;
  text: string;
  featureFlag?: FeatureFlags;
}[] = [
  { icon: "info",            routerLink: "projectInfo/overview",   text: $localize`:@@overview:Overview` },
  { icon: "route",           routerLink: "projectInfo/schedule",   text: $localize`:@@projectSchedule:Project Schedule` },
  { icon: "tasks",           routerLink: "tasks",                  text: $localize`:@@tasks:Tasks` },
  { icon: "check-list",      routerLink: "microtasks",             text: $localize`:@@microtasks:Microtasks` },
  { icon: "folder",          routerLink: "documents",              text: $localize`:@@documents:Documents` },
  { icon: "dashboard-square",routerLink: "project-hub",            text: $localize`:@@projectHub:Project Hub`,              featureFlag: FeatureFlags.ProjectHub },
  { icon: "coins-euro",      routerLink: "project-costs",          text: $localize`:@@projectCosts:Costs`,          featureFlag: FeatureFlags.ProjectCost },
  { icon: "layers",          routerLink: "projectInfo/components", text: $localize`:@@technicalComponents:Components`, featureFlag: FeatureFlags.TechnicalComponents },
  { icon: "calendar",        routerLink: "calendar",               text: $localize`:@@calendar:Calendar` },
  { icon: "user-multiple",   routerLink: "project-contacts",       text: $localize`:@@contacts: Contacts` },
  { icon: "database",        routerLink: "projectInfo/mastr",      text: $localize`:@@mastr:MaStR`,                 featureFlag: FeatureFlags.MaStR },
  { icon: "album",           routerLink: "gallery",                text: $localize`:@@gallery:Gallery` },
];

@Component({
    selector: "app-project.page",
    imports: [
        DxTextBoxModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxCheckBoxModule,
        DxNumberBoxModule,
        DxFileUploaderModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxContextMenuModule,
        DxActionSheetModule,
        DxScrollViewModule,
        CommonModule,
        SharedComponentsModule,
        SharedPipesModule,
        DxTabsModule,
        RouterModule,
        DxToastModule,
    ],
    providers: [ProjectTaskService],
    templateUrl: "./project.page.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./project.page.css"]
})
export class ProjectPage implements OnInit, OnDestroy {
  public static ProjectPageInstance: ProjectPage = null;

  globalProjectInfo: any = { isFavorite: false };
  globalProjectInfoLoaded: boolean = false;

  favoriteToastVisible: boolean = false;
  favoriteMessage = "";
  favoriteToastType = "";

  commentCount: number = 0;
  tabsVisible: boolean = false;

  projectId: number = null;
  siteId = 0;
  private alive = true;

  activeHeaderTab = 1;

  projectTabs: {
    icon: IconName;
    routerLink: string;
    text: string;
    hasNoPermission?: boolean;
  }[] = [];

  private $unsubscribe = new Subject();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private projectService: ProjectService,
    private projectInfoService: ProjectInfoService,
    private ngZone: NgZone,
    private utilities: UtilitiesService,
    private gridConnectionService: GridConnectionService,
    private appGlobals: AppGlobals,
    private pageNavService: PageNavigationService
  ) {
    ProjectPage.ProjectPageInstance = this;
    // Build projectTabs with feature flag logic

    router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        if (val.url.includes("site-overview")) {
          this.activeHeaderTab = 1;
        } else {
          this.activeHeaderTab = 2;
        }
      }
    });
  }

  private get projectBasePath(): string {
    const segments: string[] = [];
    this.activatedRoute.pathFromRoot.forEach(route =>
      route.snapshot.url.forEach(s => segments.push(s.path))
    );
    return '/' + segments.join('/');
  }

  private buildAndPublishTabs(): void {
    const basePath = this.projectBasePath;
    this.projectTabs = projectNavConfig.map((tab) => ({
      ...tab,
      routerLink: `${basePath}/${tab.routerLink}`,
      hasNoPermission: tab.featureFlag
        ? !AppGlobals.UserCan(tab.featureFlag)
        : false,
    }));
    this.pageNavService.setSecondaryTabs(this.projectTabs);
  }

  ngOnInit(): void {
    this.buildAndPublishTabs();

    this.appGlobals.$features
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(() => {
        this.buildAndPublishTabs();
      });

    // Number, not the raw string param: the paramMap guard below compares strictly, so a
    // string here makes it fire a second, identical getGlobalProjectInfo on every page load.
    this.projectId = +this.activatedRoute.snapshot.params.id;

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId && Number(newId) !== this.projectId) {
          this.projectId = Number(newId);
          this.gridConnectionService.updateProjectId(this.projectId);
          this.globalProjectInfoLoaded = false;
          this.loadGlobalProjectInfo();
          this.buildAndPublishTabs();
        }
      });

    const siteIdFromUrl = this.activatedRoute.snapshot.params.siteId;
    if (siteIdFromUrl) {
      this.siteId = +siteIdFromUrl;
    } else {
      this.utilities
        .getSiteIdByProjectId({ ProjectId: this.projectId })
        .then((result) => {
          this.siteId = result.siteId;
          const currentUrl = this.router.url;
          const newUrl = currentUrl.replace(
            /\/site\/[^/]+\/project\/(\d+)/,
            `/site/${this.siteId}/project/$1`
          );
          this.router.navigateByUrl(newUrl, { replaceUrl: true }).then(() => {
            this.buildAndPublishTabs();
          });
        });
    }

    this.gridConnectionService.updateProjectId(this.projectId);

    this.globalProjectInfoLoaded = false;
    this.loadGlobalProjectInfo();

    this.RefreshCommentTabs();
  }

  loadGlobalProjectInfo(): void {
    const forProjectId = this.projectId;
    this.projectInfoService
      .getGlobalProjectInfo({ ProjectId: this.projectId })
      .then((result) => {
        if (!this.alive || this.projectId !== forProjectId) return;
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.globalProjectInfo = result.globalProjectInfoResultItem;
          this.globalProjectInfoLoaded = true;
          const { nr, title } = this.globalProjectInfo;
          const label = nr && title ? `${nr} | ${title}` : (title ?? nr ?? null);
          this.appGlobals.$currentProjectName.next(label);

          this.ngZone.runOutsideAngular(() => {
            if (
              this.globalProjectInfo.phase != null &&
              this.globalProjectInfo.title != null &&
              this.globalProjectInfo.nr != null
            ) {
              this.appGlobals.$headerTitle.next(
                `${this.globalProjectInfo.nr} ${this.globalProjectInfo.title} | ${this.globalProjectInfo.phase}`
              );
            } else {
              if (this.globalProjectInfo.phase === null) {
                this.appGlobals.$headerTitle.next(
                  `${this.globalProjectInfo.nr} ${this.globalProjectInfo.title}`
                );
              } else {
                if (this.globalProjectInfo.title === null) {
                  this.appGlobals.$headerTitle.next(
                    `${this.globalProjectInfo.nr} | ${this.globalProjectInfo.phase}`
                  );
                } else {
                  if (this.globalProjectInfo.nr === null) {
                    this.appGlobals.$headerTitle.next(
                      `${this.globalProjectInfo.title} | ${this.globalProjectInfo.phase}`
                    );
                  }
                }
              }
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.pageNavService.clearSecondaryTabs();
    this.$unsubscribe.next(undefined);
    this.$unsubscribe.complete();
  }

  addProjectAsFavorite() {
    //  if (this.globalProjectInfo) this.globalProjectInfo.isFavorite = true;

    this.projectService
      .addProjectAsFavorite({ ProjectId: this.projectId })
      .then((result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else this.showFavoriteToast($localize`added to favorites`, "success");
      });
  }

  removeProjectAsFavorite() {
    // if (this.globalProjectInfo) this.globalProjectInfo.isFavorite = false;

    this.projectService
      .removeProjectAsFavorite({ ProjectId: this.projectId })
      .then((result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else this.showFavoriteToast($localize`removed from favorites`, "error");
      });
  }

  toggleProjectFavorite(): void {
    if (this.globalProjectInfo.isFavorite == undefined)
      this.globalProjectInfo.isFavorite = false;

    this.globalProjectInfo.isFavorite =
      this.globalProjectInfo.isFavorite === true ? false : true;

    if (this.globalProjectInfo.isFavorite !== true)
      this.removeProjectAsFavorite();
    else this.addProjectAsFavorite();
  }

  showFavoriteToast(message, type) {
    this.favoriteMessage = message;
    this.favoriteToastType = type;
    this.favoriteToastVisible = true;
  }

  handleTabLoad(e: any, index: number): void {
    if (index != 3) return;
    if (this.commentCount === 0) return;
    e.element.children[0].innerHTML += `<span class='tab-comment-counter gt-margin-small-left' >${this.commentCount}</span>`;
  }

  public RefreshCommentTabs() {
    this.tabsVisible = false;
    this.projectService
      .getCommentCount({ CommentArea: "project", ProjectId: this.projectId })
      .then(
        (result) => {
          if (result.hasError === true)
            AppGlobals.ShowErrorMessage(result.feedback);
          else {
            this.tabsVisible = true;
            this.commentCount = result.commentCount;
          }
        },
        (error: any) => {
          AppGlobals.ShowErrorMessage(error);
        }
      );
  }

  checkIfVisible() {
    let route = this.router.url;

    if (
      route.includes("site-overview") ||
      route.includes("properties") ||
      route.includes("infrastructure")
    ) {
      return false;
    }

    return true;
  }

  setActiveHeaderTab(id: any) {
    this.activeHeaderTab = id;
  }

  checkIfActive(id): any {
    if (this.activeHeaderTab == id) {
      return true;
    }
  }
}
